import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, Mail, Phone, Globe, Save, 
  CreditCard, Shield, UserCheck, UserX, 
  Edit, Download, Activity, Zap,
  Wallet, Clock, AlertCircle, CheckCircle,
  XCircle, FileText, Copy, Send, Eye,
  User, MapPin, CreditCard as Card,
  ShieldCheck, Smartphone, DollarSign,
  TrendingUp, TrendingDown, Percent,
  Filter, MoreVertical, RefreshCw,
  Shield as ShieldIcon, Bell, Lock,
  Target, PieChart, Users as UsersIcon,
  Calendar as CalendarIcon
} from 'lucide-react';
import { AdminUsersAPI, AdminAPIUtils } from '../services/api';

function UserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [actionLoading, setActionLoading] = useState(false);
  const [stats, setStats] = useState({
    depositCount: 0,
    withdrawalCount: 0,
    totalWagered: 0,
    totalWon: 0,
    winRate: 0
  });

  useEffect(() => {
    fetchUser();
    fetchUserStats();
  }, [userId]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab && ['overview', 'transactions', 'kyc', 'wallet', 'edit', 'activity'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [location]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const data = await AdminUsersAPI.getUser(userId);
      setUser(data);
    } catch (err) {
      setError(AdminAPIUtils.handleError(err, false).message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const data = await AdminUsersAPI.getUserStats(userId);
      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch user stats:', err);
    }
  };

  const updateUserStatus = async (status) => {
    if (!window.confirm(`Are you sure you want to ${status} this user?`)) return;

    try {
      setActionLoading(true);
      await AdminUsersAPI.updateUserStatus(userId, status);
      fetchUser(); // Refresh user data
    } catch (err) {
      AdminAPIUtils.handleError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const resetPassword = async () => {
    const newPassword = prompt('Enter new password (minimum 8 characters):');
    if (!newPassword || newPassword.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }

    if (!window.confirm('Are you sure you want to reset this user\'s password?')) return;

    try {
      setActionLoading(true);
      await AdminUsersAPI.resetUserPassword(userId, newPassword);
      alert('Password reset successfully');
    } catch (err) {
      AdminAPIUtils.handleError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', icon: <CheckCircle className="w-3 h-3" /> },
      pending: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', icon: <Clock className="w-3 h-3" /> },
      suspended: { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/20', icon: <UserX className="w-3 h-3" /> },
      banned: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20', icon: <XCircle className="w-3 h-3" /> }
    };
    
    const configItem = config[status] || config.banned;
    
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${configItem.bg} ${configItem.text} border ${configItem.border} flex items-center gap-1.5`}>
        {configItem.icon}
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getKycBadge = (status) => {
    const config = {
      verified: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', icon: <ShieldCheck className="w-3 h-3" /> },
      pending: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', icon: <Clock className="w-3 h-3" /> },
      rejected: { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/20', icon: <XCircle className="w-3 h-3" /> },
      not_submitted: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20', icon: <Shield className="w-3 h-3" /> }
    };
    
    const configItem = config[status] || config.not_submitted;
    const displayText = status?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${configItem.bg} ${configItem.text} border ${configItem.border} flex items-center gap-1.5`}>
        {configItem.icon}
        {displayText}
      </span>
    );
  };

  const getTransactionBadge = (type) => {
    const config = {
      deposit: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
      withdrawal: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
      win: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
      bet: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' }
    };
    
    const configItem = config[type] || { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20' };
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${configItem.bg} ${configItem.text} ${configItem.border}`}>
        {type}
      </span>
    );
  };

  const statCards = [
    {
      title: 'Wallet Balance',
      value: `₿${AdminAPIUtils.formatBTC(user?.wallet_info?.[0]?.balance || 0)}`,
      icon: <Wallet className="w-5 h-5" />,
      color: 'from-blue-500 to-cyan-500',
      change: '2.5%',
      changeText: 'from yesterday',
      trend: 'up'
    },
    {
      title: 'Total Deposits',
      value: `₿${AdminAPIUtils.formatBTC(user?.transaction_stats?.deposit?.total_amount || 0)}`,
      icon: <CreditCard className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-500',
      change: `${user?.transaction_stats?.deposit?.count || 0} deposits`,
      changeText: 'all time',
      trend: 'up'
    },
    {
      title: 'Total Wagered',
      value: `₿${AdminAPIUtils.formatBTC(stats.totalWagered || 0)}`,
      icon: <Activity className="w-5 h-5" />,
      color: 'from-violet-500 to-purple-500',
      change: `${stats.winRate}%`,
      changeText: 'win rate',
      trend: stats.winRate >= 50 ? 'up' : 'down'
    },
    {
      title: 'Total Won',
      value: `₿${AdminAPIUtils.formatBTC(stats.totalWon || 0)}`,
      icon: <DollarSign className="w-5 h-5" />,
      color: 'from-amber-500 to-yellow-500',
      change: `$${AdminAPIUtils.formatBTC((stats.totalWon || 0) - (stats.totalWagered || 0))}`,
      changeText: 'net profit',
      trend: (stats.totalWon || 0) > (stats.totalWagered || 0) ? 'up' : 'down'
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-deep-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-sky-500 mx-auto mb-6"></div>
          <p className="text-charcoal-gray">Loading user details...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex justify-center items-center h-screen bg-deep-black">
        <div className="text-center">
          <AlertCircle className="w-20 h-20 text-rose-400 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-white mb-4">
            {error ? 'Error Loading User' : 'User Not Found'}
          </h2>
          <p className="text-charcoal-gray mb-8 max-w-md mx-auto">
            {error || 'The user you are looking for does not exist or has been removed.'}
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate('/users')}
              className="px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-105"
            >
              Back to Users
            </button>
            <button
              onClick={fetchUser}
              className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/users')}
            className="p-2 bg-gray-800/50 hover:bg-gray-700 text-charcoal-gray hover:text-white rounded-xl transition-all duration-300 hover:scale-105"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
              User Profile
            </h1>
            <p className="text-charcoal-gray mt-1">Manage and monitor user account</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {user.status === 1 ? (
            <button
              onClick={() => updateUserStatus('suspended')}
              disabled={actionLoading}
              className="px-4 py-2.5 bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white rounded-xl disabled:opacity-50 transition-all duration-300 hover:scale-[1.02] flex items-center gap-2"
            >
              <UserX className="w-4 h-4" />
              Suspend User
            </button>
          ) : (
            <button
              onClick={() => updateUserStatus('active')}
              disabled={actionLoading}
              className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl disabled:opacity-50 transition-all duration-300 hover:scale-[1.02] flex items-center gap-2"
            >
              <UserCheck className="w-4 h-4" />
              Activate User
            </button>
          )}
          <button
            onClick={() => setActiveTab('edit')}
            className="px-4 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Edit User
          </button>
          <button
            onClick={fetchUser}
            className="p-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300 hover:scale-105"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* User Profile Header */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
        <div className="flex flex-col lg:flex-row lg:items-start gap-6">
          <div className="flex-shrink-0">
            <div className="relative">
              <div className="h-24 w-24 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-3xl shadow-xl">
                {user.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-full border-4 border-night-blue flex items-center justify-center ${
                user.status === 1 ? 'bg-emerald-500' : 
                user.status === 2 ? 'bg-amber-500' : 
                'bg-gray-500'
              }`}>
                {user.status === 1 ? <CheckCircle className="w-4 h-4 text-white" /> :
                 user.status === 2 ? <Clock className="w-4 h-4 text-white" /> :
                 <XCircle className="w-4 h-4 text-white" />}
              </div>
            </div>
          </div>
          <div className="flex-1">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-white">{user.username}</h2>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  <div className="flex items-center gap-2 text-charcoal-gray">
                    <Mail className="w-4 h-4" />
                    <span className="truncate max-w-[200px]">{user.email}</span>
                    <button
                      onClick={() => copyToClipboard(user.email)}
                      className="text-charcoal-gray hover:text-white transition-colors"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                  {user.phone_number && (
                    <div className="flex items-center gap-2 text-charcoal-gray">
                      <Phone className="w-4 h-4" />
                      <span>{user.phone_number}</span>
                    </div>
                  )}
                  {user.country && (
                    <div className="flex items-center gap-2 text-charcoal-gray">
                      <Globe className="w-4 h-4" />
                      <span>{user.country}</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-4">
                  {getStatusBadge(user.status === 1 ? 'active' : user.status === 2 ? 'suspended' : user.status === 3 ? 'banned' : 'pending')}
                  {getKycBadge(user.kyc_info?.status === 1 ? 'verified' : user.kyc_info?.status === 0 ? 'pending' : user.kyc_info?.status === 2 ? 'rejected' : 'not_submitted')}
                  <span className="text-sm text-charcoal-gray">
                    Joined {AdminAPIUtils.formatDate(user.created_at, 'date')}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={resetPassword}
                  disabled={actionLoading}
                  className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white rounded-xl disabled:opacity-50 transition-all duration-300 hover:scale-[1.02] flex items-center gap-2"
                >
                  <ShieldIcon className="w-4 h-4" />
                  Reset Password
                </button>
                <button className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div 
            key={index} 
            className="bg-night-blue/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:bg-night-blue/70 transition-all duration-300 hover:scale-[1.02] group"
          >
            <div className="flex items-start justify-between mb-6">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${card.color} shadow-lg`}>
                {card.icon}
              </div>
              {card.trend === 'up' && (
                <div className="flex items-center text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">{card.change}</span>
                </div>
              )}
              {card.trend === 'down' && (
                <div className="flex items-center text-rose-400 bg-rose-400/10 px-3 py-1 rounded-full">
                  <TrendingDown className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">{card.change}</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold text-white">{card.value}</p>
              <p className="text-charcoal-gray text-sm uppercase tracking-wider">{card.title}</p>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-800">
              <p className="text-sm text-charcoal-gray">
                <span className={`font-medium ${card.trend === 'up' ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {card.change}
                </span>{' '}
                {card.changeText}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Content */}
        <div className="lg:col-span-2">
          {/* Tabs */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden">
            <div className="border-b border-gray-800">
              <nav className="flex overflow-x-auto">
                {['overview', 'transactions', 'kyc', 'wallet', 'activity', 'edit'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-6 py-4 font-medium text-sm whitespace-nowrap transition-colors relative ${
                      activeTab === tab
                        ? 'text-white border-b-2 border-sky-500'
                        : 'text-charcoal-gray hover:text-white hover:bg-gray-800/30'
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Personal Information */}
                  <div>
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-charcoal-gray mb-2">Full Name</label>
                          <div className="text-white">{user.full_name || 'Not provided'}</div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-charcoal-gray mb-2">Date of Birth</label>
                          <div className="text-white">{user.date_of_birth || 'Not provided'}</div>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-charcoal-gray mb-2">Country</label>
                          <div className="flex items-center gap-2 text-white">
                            <MapPin className="w-4 h-4" />
                            {user.country || 'Not provided'}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-charcoal-gray mb-2">Phone Number</label>
                          <div className="flex items-center gap-2 text-white">
                            <Phone className="w-4 h-4" />
                            {user.phone_number || 'Not provided'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Account Information */}
                  <div>
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Account Information
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between py-3 border-b border-gray-800/50">
                        <span className="text-sm text-charcoal-gray">User ID</span>
                        <div className="flex items-center gap-2">
                          <code className="font-mono text-sm text-gray-300">{user.id}</code>
                          <button
                            onClick={() => copyToClipboard(user.id)}
                            className="text-charcoal-gray hover:text-white transition-colors p-1 hover:bg-gray-800/50 rounded-lg"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between py-3 border-b border-gray-800/50">
                        <span className="text-sm text-charcoal-gray">Email Verified</span>
                        <div className="flex items-center gap-2">
                          {user.email_verified ? (
                            <>
                              <CheckCircle className="w-4 h-4 text-emerald-400" />
                              <span className="text-sm text-emerald-400">Verified</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="w-4 h-4 text-rose-400" />
                              <span className="text-sm text-rose-400">Not Verified</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between py-3 border-b border-gray-800/50">
                        <span className="text-sm text-charcoal-gray">Last Login</span>
                        <span className="text-sm text-white">
                          {user.last_login_at ? AdminAPIUtils.formatDate(user.last_login_at, 'datetime') : 'Never'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-3">
                        <span className="text-sm text-charcoal-gray">Account Created</span>
                        <span className="text-sm text-white">
                          {AdminAPIUtils.formatDate(user.created_at, 'datetime')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'transactions' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <Activity className="w-5 h-5" />
                      Recent Transactions
                    </h3>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-charcoal-gray w-4 h-4" />
                        <select className="pl-10 pr-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors">
                          <option value="all">All Types</option>
                          <option value="deposit">Deposits</option>
                          <option value="withdrawal">Withdrawals</option>
                          <option value="bet">Bets</option>
                          <option value="win">Wins</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  {user.recent_transactions?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-800">
                        <thead className="bg-gray-900/50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                          {user.recent_transactions.slice(0, 10).map((tx) => (
                            <tr key={tx.id} className="hover:bg-gray-800/30 transition-colors">
                              <td className="px-6 py-4">
                                {getTransactionBadge(tx.transaction_type)}
                              </td>
                              <td className="px-6 py-4">
                                <div className="font-medium text-white">
                                  BTC{AdminAPIUtils.formatBTC(tx.amount)}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                                  tx.status === 1 ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                                  tx.status === 0 ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                                  'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                                }`}>
                                  {tx.status === 1 ? 'Completed' : tx.status === 0 ? 'Pending' : 'Failed'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-charcoal-gray">
                                {AdminAPIUtils.formatDate(tx.created_at, 'datetime')}
                              </td>
                              <td className="px-6 py-4">
                                <button className="p-2 bg-gray-800/50 hover:bg-gray-700 text-charcoal-gray hover:text-white rounded-lg transition-colors">
                                  <Eye className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <FileText className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">No transactions found</h3>
                      <p className="text-charcoal-gray">This user has no transaction history</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'kyc' && (
                <div>
                  <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5" />
                    KYC Information
                  </h3>
                  {user.kyc_info ? (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-charcoal-gray mb-2">KYC Status</label>
                            {getKycBadge(user.kyc_info.status === 1 ? 'verified' : user.kyc_info.status === 0 ? 'pending' : 'rejected')}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-charcoal-gray mb-2">Submitted Date</label>
                            <div className="text-white">
                              {AdminAPIUtils.formatDate(user.kyc_info.created_at, 'datetime')}
                            </div>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-charcoal-gray mb-2">Verified Date</label>
                            <div className="text-white">
                              {user.kyc_info.verified_at ? AdminAPIUtils.formatDate(user.kyc_info.verified_at, 'datetime') : 'Not verified'}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-charcoal-gray mb-2">Document Type</label>
                            <div className="text-white">
                              {user.kyc_info.document_type || 'Not specified'}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {user.kyc_info.rejection_reason && (
                        <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4">
                          <h4 className="font-medium text-rose-400 mb-2">Rejection Reason</h4>
                          <p className="text-rose-300">{user.kyc_info.rejection_reason}</p>
                        </div>
                      )}
                      
                      <div className="flex gap-3 pt-6 border-t border-gray-800">
                        <button className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          View Documents
                        </button>
                        <button className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center gap-2">
                          <Download className="w-4 h-4" />
                          Download Documents
                        </button>
                        {user.kyc_info.status === 0 && (
                          <button className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            Approve KYC
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Shield className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">KYC not submitted</h3>
                      <p className="text-charcoal-gray mb-6">This user has not submitted KYC documents</p>
                      <button className="px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-105 flex items-center gap-2 mx-auto">
                        <Send className="w-4 h-4" />
                        Request KYC Submission
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'edit' && (
                <div>
                  <h3 className="text-lg font-bold text-white mb-6">Edit User Information</h3>
                  <form className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-charcoal-gray mb-2">Full Name</label>
                        <input
                          type="text"
                          defaultValue={user.full_name}
                          className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                          placeholder="Full Name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-charcoal-gray mb-2">Email</label>
                        <input
                          type="email"
                          defaultValue={user.email}
                          className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                          placeholder="Email Address"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-charcoal-gray mb-2">Phone Number</label>
                        <input
                          type="tel"
                          defaultValue={user.phone_number}
                          className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                          placeholder="Phone Number"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-charcoal-gray mb-2">Country</label>
                        <select
                          defaultValue={user.country}
                          className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                        >
                          <option value="">Select Country</option>
                          <option value="US">United States</option>
                          <option value="UK">United Kingdom</option>
                          <option value="CA">Canada</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-charcoal-gray mb-2">Account Status</label>
                        <select
                          defaultValue={user.status}
                          className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                        >
                          <option value="1">Active</option>
                          <option value="0">Inactive</option>
                          <option value="2">Suspended</option>
                          <option value="3">Banned</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-charcoal-gray mb-2">KYC Status</label>
                        <select
                          defaultValue={user.kyc_info?.status || 'not_submitted'}
                          className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                        >
                          <option value="not_submitted">Not Submitted</option>
                          <option value="0">Pending</option>
                          <option value="1">Verified</option>
                          <option value="2">Rejected</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-800">
                      <button
                        type="button"
                        onClick={() => setActiveTab('overview')}
                        className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center gap-2"
                      >
                        <Save className="w-4 h-4" />
                        Save Changes
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Quick Actions & Stats */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Quick Actions
            </h3>
            <div className="space-y-3">
              <button className="w-full px-4 py-3.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center justify-between group">
                <span>Add Funds</span>
                <CreditCard className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={resetPassword}
                className="w-full px-4 py-3.5 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center justify-between group"
              >
                <span>Reset Password</span>
                <Lock className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="w-full px-4 py-3.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center justify-between group">
                <span>Send Message</span>
                <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="w-full px-4 py-3.5 bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center justify-between group">
                <span>View Activity</span>
                <Activity className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Performance Stats */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Performance Stats
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-charcoal-gray">Win Rate</span>
                  <span className="text-sm font-bold text-emerald-400">{stats.winRate}%</span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-emerald-500 to-green-600 h-2 rounded-full" 
                    style={{ width: `${Math.min(stats.winRate, 100)}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-charcoal-gray">Avg Bet Size</span>
                  <span className="text-sm font-bold text-blue-400">
                    BTC{AdminAPIUtils.formatBTC((stats.totalWagered || 0) / Math.max(stats.betCount || 1, 1))}
                  </span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-cyan-600 h-2 rounded-full" 
                    style={{ width: '65%' }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-charcoal-gray">Profit/Loss</span>
                  <span className={`text-sm font-bold ${
                    (stats.totalWon || 0) > (stats.totalWagered || 0) ? 'text-emerald-400' : 'text-rose-400'
                  }`}>
                    BTC{AdminAPIUtils.formatBTC((stats.totalWon || 0) - (stats.totalWagered || 0))}
                  </span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      (stats.totalWon || 0) > (stats.totalWagered || 0) 
                        ? 'bg-gradient-to-r from-emerald-500 to-green-600' 
                        : 'bg-gradient-to-r from-rose-500 to-red-600'
                    }`}
                    style={{ width: `${Math.min(Math.abs((stats.totalWon || 0) / Math.max(stats.totalWagered || 1, 1)) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Account Security */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" />
              Account Security
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-gray-800/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-800/50 rounded-lg">
                    <Smartphone className="w-4 h-4 text-sky-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">2FA Enabled</p>
                    <p className="text-xs text-charcoal-gray">Two-factor authentication</p>
                  </div>
                </div>
                {user.two_factor_enabled ? (
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-rose-400" />
                )}
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-800/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-800/50 rounded-lg">
                    <Mail className="w-4 h-4 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">Email Verified</p>
                    <p className="text-xs text-charcoal-gray">Email confirmation</p>
                  </div>
                </div>
                {user.email_verified ? (
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-rose-400" />
                )}
              </div>
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-800/50 rounded-lg">
                    <Clock className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">Last Password Change</p>
                    <p className="text-xs text-charcoal-gray">Security update</p>
                  </div>
                </div>
                <span className="text-xs text-charcoal-gray">
                  {user.last_password_change ? AdminAPIUtils.timeAgo(user.last_password_change) : 'Never'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserDetail;